import axios, { InternalAxiosRequestConfig } from 'axios';

// Token in memoria — mai in localStorage
let _authToken: string | null = null;

export function setAuthToken(token: string | null) {
  _authToken = token;
}

const API = axios.create({
  baseURL: (process.env.REACT_APP_API_URL || 'https://firmereconcilia.it') + '/api',
  withCredentials: true,  // necessario per il cookie HttpOnly del refresh token
});

// Aggiunge automaticamente il token JWT a ogni chiamata
API.interceptors.request.use((config) => {
  if (_authToken) {
    config.headers.Authorization = `Bearer ${_authToken}`;
  }
  return config;
});

// Se il token scade: tenta refresh automatico, poi reindirizza al login
API.interceptors.response.use(
  response => response,
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const res = await axios.post(
          (process.env.REACT_APP_API_URL || 'https://firmereconcilia.it') + '/api/auth/refresh',
          {},
          { withCredentials: true }
        );
        const newToken: string = res.data.access_token;
        setAuthToken(newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return API(originalRequest);
      } catch {
        setAuthToken(null);
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// PROCEDURE
export const getProcedure = () => API.get('/procedure/');
export const getProcedura = (id: number) => API.get(`/procedure/${id}`);
export const creaProc = (dati: any) => API.post('/procedure/', dati);
export const modificaProc = (id: number, dati: any) => API.patch(`/procedure/${id}`, dati);
export const eliminaProcedura = (id: number) => API.delete(`/procedure/${id}`);
export const ripristinaProcedura = (id: number) => API.post(`/procedure/${id}/ripristina`);
export const getCestino = () => API.get('/cestino');

// VERBALI
export const getVerbali = (procedura_id: number) => API.get(`/procedure/${procedura_id}/verbali`);
export const getVerbale = (verbale_id: number) => API.get(`/verbali/${verbale_id}`);
export const creaVerbale = (dati: any) => API.post('/verbali/', dati);
export const eliminaVerbale = (verbale_id: number) => API.delete(`/verbali/${verbale_id}`);
export const ripristinaVerbale = (id: number) => API.post(`/verbali/${id}/ripristina`);

export const uploadPdfVerbale = (verbale_id: number, file: File) => {
  const form = new FormData();
  form.append('file', file);
  return API.post(`/verbali/${verbale_id}/upload-pdf`, form);
};

// GIRO FIRME
export const avviaGiroFirme = (verbale_id: number) =>
  API.post(`/verbali/${verbale_id}/avvia-giro-firme`);

// MEDIATORE
export const uploadPdfMediatore = (verbale_id: number, file: File) => {
  const form = new FormData();
  form.append('file', file);
  return API.post(`/verbali/${verbale_id}/upload-mediatore`, form);
};

export const downloadPdfCorrente = (verbale_id: number) =>
  API.get(`/verbali/${verbale_id}/download-pdf-corrente`, { responseType: 'blob' });

// NOTIFICHE
export const inviaVerbaleFinale = (verbale_id: number) =>
  API.post(`/verbali/${verbale_id}/invia-verbale-finale`);

// FIRMATARI
export const saltaFirmatario = (firmatario_id: number) =>
  API.post(`/firmatari-verbale/${firmatario_id}/salta`);

// RICEVUTE PEC
export const getRicevute = (verbale_id: number) =>
  API.get(`/verbali/${verbale_id}/ricevute`);

export const downloadAccettazione = (invio_id: number) =>
  API.get(`/invii-pec/${invio_id}/download-accettazione`, { responseType: 'blob' });

export const downloadConsegna = (invio_id: number) =>
  API.get(`/invii-pec/${invio_id}/download-consegna`, { responseType: 'blob' });

export const ricaricaRicevute = (invio_id: number) =>
  API.post(`/invii-pec/${invio_id}/ricarica-ricevute`);

export const archiviaInvioPec = (invio_id: number) =>
  API.post(`/invii-pec/${invio_id}/archivia`);

export const controllRicevuteFirme = (verbale_id: number) =>
  API.post(`/verbali/${verbale_id}/controlla-ricevute`);

// PDF STEPS
export const getPdfSteps = (verbale_id: number) =>
  API.get(`/verbali/${verbale_id}/pdf-steps`);

export const downloadPdfStep = (step_id: number) =>
  API.get(`/pdf-steps/${step_id}/download`, { responseType: 'blob' });

// LOG
export const getLogs = () => API.get('/auth/logs');

// SOLLECITI
export const sospiendiSolleciti = (verbale_id: number) =>
  API.post(`/verbali/${verbale_id}/sospendi-solleciti`);
export const riattivasollecitiSolleciti = (verbale_id: number) =>
  API.post(`/verbali/${verbale_id}/riattiva-solleciti`);

// MODIFICA VERBALE
export const modificaGiorniScadenza = (verbale_id: number, giorni_scadenza: number) =>
  API.patch(`/verbali/${verbale_id}/giorni-scadenza`, { giorni_scadenza });

// RUBRICA FIRMATARI
export const getRubrica = () => API.get('/firmatari/rubrica');

// MODIFICA FIRMATARI
export const modificaFirmatario = (firmatario_id: number, dati: { nome: string; email: string }) =>
  API.patch(`/firmatari-verbale/${firmatario_id}/modifica`, dati);
export const aggiungiFirmatarioVerbale = (verbale_id: number, dati: { nome: string; email: string; ordine: number }) =>
  API.post(`/verbali/${verbale_id}/aggiungi-firmatario`, dati);

// ARCHIVIO
export const getArchivio = () => API.get('/archivio');
export const downloadArchivio = (verbale_id: number) =>
  API.get(`/archivio/${verbale_id}/download`, { responseType: 'blob' });
export const eliminaDaArchivio = (verbale_id: number) =>
  API.delete(`/archivio/${verbale_id}`);

export const aggiornaDatiArchivio = (verbale_id: number, dati: { protocollo_archiviato?: string; parte1_archiviato?: string; parte2_archiviato?: string }) =>
  API.patch(`/archivio/${verbale_id}/dati`, dati);

// UTENTI
export const getMe = () => API.get('/users/me');
export const getMediatori = () => API.get('/users');
export const creaMediator = (dati: { email: string; nome?: string }) => API.post('/users', dati);
export const disabilitaUtente = (id: number) => API.delete(`/users/${id}`);
export const resetPasswordUtente = (id: number) => API.post(`/users/${id}/reset-password`);